/**
 * Bank Accounts Section
 * 
 * แสดงบัญชีธนาคารฝากและถอน (Collapsible)
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  ChevronDown,
  Eye,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/stores/currency-store'

// ============================================
// Types
// ============================================

export interface BankAccount {
  id: string
  name: string
  bankName: string
  bankId: string
  bankAccount: string
  bankImage?: string
  type: 'Deposit' | 'Withdraw' | 'Both'
  amount: number
  apiStatus: 'active' | 'suspend'
  bankApi: boolean
  accessKeyBankId?: string
  lineconnectApi?: boolean
}

interface BankAccountsSectionProps {
  banks: BankAccount[]
  onViewDepositLog?: (bankId: string) => void
  onViewWithdrawLog?: (bankId: string) => void
  onViewTrueWalletLog?: () => void
  defaultOpen?: boolean
}

// ============================================
// Main Component
// ============================================

export function BankAccountsSection({
  banks,
  onViewDepositLog,
  onViewWithdrawLog,
  onViewTrueWalletLog,
  defaultOpen = false,
}: BankAccountsSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const { format: formatCurrency } = useCurrency()
  
  const depositBanks = banks.filter(b => b.type === 'Deposit' || b.type === 'Both')
  const withdrawBanks = banks.filter(b => b.type === 'Withdraw' || b.type === 'Both')

  return (
    <Card>
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-5 h-5" />
            บัญชีธนาคาร
            <Badge variant="secondary" className="ml-2">
              {banks.length} บัญชี
            </Badge>
          </CardTitle>
          <ChevronDown className={cn(
            "w-5 h-5 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )} />
        </div>
      </CardHeader>
      
      {isOpen && (
        <CardContent className="pt-0">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Deposit Banks */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowDownToLine className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium text-emerald-600">บัญชีรับฝาก</span>
                </div>
                {depositBanks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">ไม่มีบัญชีฝาก</p>
                ) : (
                  depositBanks.map(bank => (
                    <div
                      key={bank.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-200/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-800 flex items-center justify-center border overflow-hidden">
                          {bank.bankImage ? (
                            <img src={bank.bankImage} alt={bank.bankName} className="w-8 h-8 object-contain" />
                          ) : (
                            <Building2 className="w-5 h-5 text-emerald-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{bank.bankName}</p>
                            <span className="text-xs text-primary font-medium">{bank.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">{bank.bankAccount}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{formatCurrency(bank.amount)}</p>
                        <div className="flex items-center gap-2 justify-end">
                          {bank.apiStatus === 'active' ? (
                            <span className="flex items-center text-xs text-emerald-600">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse mr-1" />
                              API
                            </span>
                          ) : (
                            <span className="text-xs text-red-600">ระงับ</span>
                          )}
                          {bank.bankApi && bank.bankId !== 'truewallet' && bank.bankId !== '88888888' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-primary"
                              onClick={() => onViewDepositLog?.(bank.accessKeyBankId || '')}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              ดูรายการ
                            </Button>
                          )}
                          {bank.bankId === 'truewallet' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-primary"
                              onClick={onViewTrueWalletLog}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              ดูรายการ
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Withdraw Banks */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowUpFromLine className="w-4 h-4 text-orange-600" />
                  <span className="font-medium text-orange-600">บัญชีถอน</span>
                </div>
                {withdrawBanks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">ไม่มีบัญชีถอน</p>
                ) : (
                  withdrawBanks.map(bank => (
                    <div
                      key={bank.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-orange-50/50 dark:bg-orange-900/10 border border-orange-200/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-800 flex items-center justify-center border overflow-hidden">
                          {bank.bankImage ? (
                            <img src={bank.bankImage} alt={bank.bankName} className="w-8 h-8 object-contain" />
                          ) : (
                            <Building2 className="w-5 h-5 text-orange-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{bank.bankName}</p>
                            <span className="text-xs text-primary font-medium">{bank.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">{bank.bankAccount}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-orange-600">{formatCurrency(bank.amount)}</p>
                        <div className="flex items-center gap-2 justify-end">
                          {bank.apiStatus === 'active' ? (
                            <span className="flex items-center text-xs text-emerald-600">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse mr-1" />
                              API
                            </span>
                          ) : (
                            <span className="text-xs text-red-600">ระงับ</span>
                          )}
                          {bank.bankApi && bank.bankId !== 'truewallet' && bank.bankId !== '88888888' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-primary"
                              onClick={() => onViewWithdrawLog?.(bank.accessKeyBankId || '')}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              ดูรายการ
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
      )}
    </Card>
  )
}

